import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener, useOnClickOutside } from 'usehooks-ts';
import MenuItem from '@/components/conversations/conversation-menu-modal/menu-item';
import useCollectionMenuContext from '@/components/user/collection-menu-modal/hook';
import useDeleteCollection from '@/hooks/collections/use-delete-collection';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { makePrompt } from '@/stores/prompter';
import copyTextToClipboard from '@/utils/copy-text';

interface Props {
  onClose: () => void;
  contextMenuType?: 'forward' | 'normal';
}

export default function CollectionMenu({ onClose, contextMenuType }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { selected, forward } = useCollectionMenuContext();
  const { mutateAsync: deleteCollection } = useDeleteCollection();

  useOnClickOutside(ref, () => onClose());
  useEventListener('wheel', () => onClose());
  useEventListener('resize', () => onClose());

  const handleForwardCollection = async () => {
    if (selected) {
      forward(selected.ID);
      onClose();
    }
  };

  const handleDeleteCollection = async () => {
    if (
      selected &&
      (await makePrompt({
        key: 'delete-collection',
        title: t('my-collection.delete'),
        content: t('my-collection.confirm-delete'),
      }))
    ) {
      await deleteCollection(selected.ID);
      onClose();
    }
  };
  return (
    <div
      ref={ref}
      className='flex-col flex rounded-lg bg-neutral-100 p-2 shadow-xl z-20'>
      {contextMenuType === 'forward' ? (
        <MenuItem
          label={t('conversation.forward.send')}
          onClick={handleForwardCollection}
        />
      ) : (
        <>
          <MenuItem
            label={t('conversation.forward.forward')}
            onClick={() => {
              forward(selected?.ID ?? 0);
              onClose();
            }}
          />
          {selected?.Type === ConversationMessageContentType.Text && (
            <MenuItem
              label={t('conversation.copy')}
              onClick={() => {
                copyTextToClipboard(selected.Content).catch(console.warn);
                onClose();
              }}
            />
          )}
          <MenuItem
            label={t('conversation.delete.delete')}
            onClick={handleDeleteCollection}
          />
        </>
      )}
    </div>
  );
}
